import { useState } from 'react';
import {
  useEnding, useRecordDeath, useRecordDeathDate, useRecordDischarge,
  type Ending, type DeathAction,
} from '../api/lifecycle.ts';
import type { FacilityOption } from '../api/places.ts';
import { FacilityPicker } from './PlacePicker.tsx';
import { CountySelect } from './CountySelect.ts';
import { useClientBanner } from '../api/queries.ts';
import { SURNAME_REFUSAL, surnameFromDisplayName, surnameMatches } from '../surnameConfirm.ts';
import { deathActionDue, deathActionHolder } from '../deathActions.ts';
import { ApiError } from '../api/client.ts';
import { formatDate } from '../dates.ts';

const field =
  'mt-1 w-full min-h-[48px] rounded-lg border border-neutral-300 bg-white px-3 ' +
  'text-base text-neutral-900';
const label = 'block text-sm font-semibold text-neutral-700';
const quiet =
  'min-h-[44px] rounded-lg border border-neutral-300 px-3 text-sm font-semibold ' +
  'text-neutral-800 hover:bg-neutral-50 active:bg-neutral-100';

function todayLocal(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * The two endings, behind a collapsed control.
 *
 * **Not beside Record a visit.** Recording a death or a discharge is
 * irreversible and starts statutory clocks, so it does not sit one misclick
 * away from the thing a case manager does forty times a week. Opening this is a
 * deliberate step, and it is an EXTRA one: it replaces none of the
 * confirmations below it — the date that is required and never defaulted, the
 * surname typed back, and the summary of what the act sets in motion, shown
 * before there is anything to press.
 *
 * Nothing here is fetched until the control is opened.
 *
 * Hidden where it makes no sense, and the server decides: no "has passed" for
 * someone already recorded as deceased, no "discharged" for someone already
 * discharged. A deceased client, and one whose guardianship has ended, can
 * still be discharged (081): each is its own event. What is already on record
 * is said first, in the status's own words.
 */
export function AdvancedPanel({ clientId }: { clientId: string }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<Form>(null);
  const ending = useEnding(clientId, open);

  return (
    <section className="panel">
      <button
        type="button"
        onClick={() => { setOpen((v) => !v); setForm(null); }}
        aria-expanded={open}
        className="panel-bar min-h-[44px] w-full cursor-pointer text-left"
      >
        <span>Ending this case</span>
        <small>{open ? 'Hide ▴' : 'Show ▾'}</small>
      </button>

      {open && (
        <div className="border-t border-neutral-200 px-4 pt-3 pb-4">
          {ending.isLoading && <p className="text-neutral-600">Loading…</p>}
          {ending.error && (
            <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-900">
              {ending.error.message}
            </p>
          )}
          {ending.data && (
            <Body
              clientId={clientId}
              ending={ending.data}
              form={form}
              setForm={setForm}
              onDone={() => { setForm(null); setOpen(false); }}
            />
          )}
        </div>
      )}
    </section>
  );
}

/** 'date' (156, A-#245): the date of a death recorded with its date unknown. */
type Form = 'death' | 'discharge' | 'date' | null;

function Body({
  clientId, ending, form, setForm, onDone,
}: {
  clientId: string;
  ending: Ending;
  form: Form;
  setForm: (f: Form) => void;
  onDone: () => void;
}) {
  // What is already on record, said first. `discharged_on` can be null for a
  // client discharged before the order became the record of it.
  const deceased = ending.date_of_death !== null
    || ending.legal_status === 'Deceased' || ending.legal_status === 'Deceased, discharged';
  const discharged = ending.discharged_on !== null
    || ending.legal_status === 'Discharged' || ending.legal_status === 'Deceased, discharged';
  const ended = ending.guardianship_ended_on !== null || ending.guardianship_end_date_unknown
    || ending.legal_status === 'Pending discharge';
  const onRecord = (deceased || discharged || ended) && (
    <div className="space-y-1 text-neutral-800">
      {deceased && (
        <p>
          Recorded as having passed
          {ending.date_of_death
            ? <> on <span className="font-semibold">{formatDate(ending.date_of_death)}</span></>
            : ', date of death not recorded'}.
        </p>
      )}
      {ended && !discharged && (
        <p>
          Pending discharge: the guardianship ended
          {ending.guardianship_ended_on
            ? <> on <span className="font-semibold">{formatDate(ending.guardianship_ended_on)}</span></>
            : ', date unknown'}. Not yet discharged by the court.
        </p>
      )}
      {discharged && (
        <p>
          Discharged
          {ending.discharged_on ? <> on <span className="font-semibold">{formatDate(ending.discharged_on)}</span></> : ''}.
        </p>
      )}
      {deceased && !discharged && ending.open_death_rules.length > 0 && (
        <p className="text-sm text-neutral-600">
          Still open: {ending.open_death_rules.map((r) => `${r.label} (due ${formatDate(r.due_date)})`).join('; ')}.
          A discharge can be recorded before they are.
        </p>
      )}
    </div>
  );

  if (form === 'date' && ending.may_record_death_date && ending.undated_death_record_id) {
    return (
      <DeathDateForm clientId={clientId} deathRecordId={ending.undated_death_record_id}
                     onCancel={() => setForm(null)} onDone={onDone} />
    );
  }
  if (form === 'discharge' && ending.may_record_discharge) {
    return <DischargeForm clientId={clientId} ending={ending} onCancel={() => setForm(null)} onDone={onDone} />;
  }
  // 156 (A-#245): where the death reads "date of death not recorded", for a reader who may record it
  const dateButton = ending.may_record_death_date && (
    <button type="button" onClick={() => setForm('date')} className={quiet}>
      Record the date of death
    </button>
  );
  if ((deceased || discharged || ended) && !ending.may_record_discharge) {
    if (!dateButton) return onRecord || null;
    return (
      <>
        {onRecord}
        <div className="mt-3 flex flex-wrap gap-2">{dateButton}</div>
      </>
    );
  }
  if (deceased || ended) {
    return (
      <>
        {onRecord}
        <div className="mt-3 flex flex-wrap gap-2">
          {dateButton}
          <button type="button" onClick={() => setForm('discharge')} className={quiet}>
            Guardian has been discharged
          </button>
        </div>
      </>
    );
  }

  if (!ending.may_record_death && !ending.may_record_discharge) {
    return (
      <p className="text-neutral-600">
        Ending a case is recorded by Legal, Compliance or the Director of Social Work.
      </p>
    );
  }

  if (form === 'death') {
    return <DeathForm clientId={clientId} ending={ending} onCancel={() => setForm(null)} onDone={onDone} />;
  }
  if (form === 'discharge') {
    return <DischargeForm clientId={clientId} ending={ending} onCancel={() => setForm(null)} onDone={onDone} />;
  }

  return (
    <>
      <p className="text-sm text-neutral-600">
        Both of these are permanent and start statutory clocks. You will see what each
        one sets in motion before anything is recorded.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {ending.may_record_death && (
          <button type="button" onClick={() => setForm('death')} className={quiet}>
            Client has passed
          </button>
        )}
        {ending.may_record_discharge && (
          <button type="button" onClick={() => setForm('discharge')} className={quiet}>
            Client has been discharged
          </button>
        )}
      </div>
    </>
  );
}

/** What it sets in motion, grouped the way the work is actually divided. */
function WhatFollows({ actions }: { actions: DeathAction[] }) {
  const byDept = actions.reduce<Record<string, DeathAction[]>>((acc, a) => {
    (acc[a.department] ??= []).push(a);
    return acc;
  }, {});
  return (
    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
      <p className="font-semibold text-neutral-900">
        This makes {actions.length} {actions.length === 1 ? 'task' : 'tasks'} across{' '}
        {Object.keys(byDept).length} departments, due from the date of death
      </p>
      <ul className="mt-1 space-y-1.5 text-sm">
        {Object.entries(byDept).map(([dept, list]) => (
          <li key={dept}>
            <span className="font-semibold text-neutral-700">{dept}</span>
            <ul className="mt-0.5 ml-3 space-y-0.5 text-neutral-700">
              {list.map((a) => (
                <li key={a.action_label}>
                  {a.action_label}
                  <span className="text-neutral-500">
                    {' '}· {deathActionDue(a)} · {deathActionHolder(a)}
                  </span>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The surname typed back on the death and discharge forms (Arthur, A-#173, option ii): the button enables only when it
 * matches the surname in the name the page already shows (its header's banner, the same cached read), by the ONE
 * definition the server also refuses with (surnameConfirm.ts, a byte-for-byte copy of api/src/surname-confirm.ts).
 * The page is sent nothing new. Where the shown name cannot be split (no first name on record), it waits only for
 * something typed and the server decides. The server refuses a wrong name whatever the page did.
 */
function useSurnameCheck(clientId: string, typed: string): { ok: boolean; wrong: boolean } {
  const banner = useClientBanner(clientId);
  const shown = banner.data ? surnameFromDisplayName(banner.data.display_name) : null;
  if (typed.trim() === '') return { ok: false, wrong: false };
  if (shown === null) return { ok: true, wrong: false };
  const ok = surnameMatches(typed, shown);
  return { ok, wrong: !ok };
}

function SurnameMismatch({ wrong }: { wrong: boolean }) {
  return wrong ? <p className="mt-1 text-sm text-neutral-700">{SURNAME_REFUSAL.message}.</p> : null;
}

function DeathForm({
  clientId, ending, onCancel, onDone,
}: { clientId: string; ending: Ending; onCancel: () => void; onDone: () => void }) {
  const record = useRecordDeath(clientId);
  // NEVER DEFAULTED. A date of death pre-filled with today is a date somebody
  // did not choose, on a record that will be read in court.
  const [date, setDate] = useState('');
  const [surname, setSurname] = useState('');
  const [whereKind, setWhereKind] = useState<'facility' | 'other'>('facility');
  const [facility, setFacility] = useState<FacilityOption | null>(null);
  const [other, setOther] = useState('');
  const [cause, setCause] = useState('');
  const [informant, setInformant] = useState('');

  const check = useSurnameCheck(clientId, surname);
  const ready = date.length === 10 && check.ok;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!ready) return;
        record.mutate(
          {
            confirm_last_name: surname.trim(),
            date_of_death: date,
            ...(whereKind === 'facility' && facility ? { place_facility_id: facility.id } : {}),
            ...(whereKind === 'other' && other.trim() ? { place_other: other.trim() } : {}),
            ...(cause.trim() ? { cause_of_death: cause.trim() } : {}),
            ...(informant.trim() ? { informant_name: informant.trim() } : {}),
          },
          { onSuccess: onDone },
        );
      }}
    >
      <h3 className="font-semibold text-neutral-900">Record that the client has passed</h3>

      <WhatFollows actions={ending.death_actions} />

      <label className={`${label} mt-3`} htmlFor="death-date">Date of death</label>
      <input id="death-date" type="date" className={field} value={date} max={todayLocal()}
             onChange={(e) => setDate(e.target.value)} />
      <p className="mt-1 text-xs text-neutral-500">Required, and deliberately not filled in.</p>

      <label className={`${label} mt-3`} htmlFor="death-where">Where</label>
      <div className="mt-1 flex gap-2">
        <button type="button" onClick={() => setWhereKind('facility')}
                className={`${quiet} ${whereKind === 'facility' ? '!bg-neutral-900 !text-white' : ''}`}>
          A facility
        </button>
        <button type="button" onClick={() => setWhereKind('other')}
                className={`${quiet} ${whereKind === 'other' ? '!bg-neutral-900 !text-white' : ''}`}>
          Somewhere else
        </button>
      </div>
      {whereKind === 'facility' ? (
        <FacilityPicker id="death-where" searchLabel="Which facility (optional)"
                        selected={facility} onSelect={setFacility} />
      ) : (
        <input id="death-where" className={field} value={other} maxLength={160}
               onChange={(e) => setOther(e.target.value)} placeholder="At home, in hospital…" />
      )}

      <label className={`${label} mt-3`} htmlFor="death-cause">Cause, if known</label>
      <input id="death-cause" className={field} value={cause} maxLength={160}
             onChange={(e) => setCause(e.target.value)} />

      <label className={`${label} mt-3`} htmlFor="death-informant">Who told us</label>
      <input id="death-informant" className={field} value={informant} maxLength={128}
             onChange={(e) => setInformant(e.target.value)} placeholder="Name" />

      {/* The last gate, and the one that costs something: typed, not ticked. */}
      <label className={`${label} mt-4`} htmlFor="death-surname">
        Type the client’s surname to confirm
      </label>
      <input id="death-surname" className={field} value={surname} autoComplete="off"
             onChange={(e) => setSurname(e.target.value)} placeholder="Surname" />
      <SurnameMismatch wrong={check.wrong} />
      <p className="mt-1 text-xs text-neutral-500">
        This cannot be undone from here.
      </p>

      <Failure error={record.error} />
      <div className="mt-3 flex gap-2">
        <button type="submit" disabled={!ready || record.isPending}
                className="min-h-[48px] flex-1 rounded-lg bg-neutral-900 px-4 font-semibold text-white
                           disabled:bg-neutral-200 disabled:text-neutral-600 disabled:cursor-not-allowed">
          {record.isPending ? 'Recording…' : 'Record that the client has passed'}
        </button>
        <button type="button" onClick={onCancel} className={quiet}>Cancel</button>
      </div>
    </form>
  );
}

/**
 * 156 (A-#245): the date of a death recorded with its date unknown, ONCE. The date is never defaulted, the surname is
 * typed back as for a death, and a reason says where the date came from. What it raises is the server's: the statement
 * of death and the final report, overdue at once for an old death, and a Final already on file matched to it.
 */
function DeathDateForm({
  clientId, deathRecordId, onCancel, onDone,
}: { clientId: string; deathRecordId: string; onCancel: () => void; onDone: () => void }) {
  const record = useRecordDeathDate(clientId);
  const [date, setDate] = useState('');
  const [surname, setSurname] = useState('');
  const [reason, setReason] = useState('');
  const check = useSurnameCheck(clientId, surname);
  const ready = date.length === 10 && check.ok && reason.trim() !== '';

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!ready) return;
        record.mutate(
          { death_record_id: deathRecordId, date_of_death: date, confirm_last_name: surname.trim(), reason: reason.trim() },
          { onSuccess: onDone },
        );
      }}
    >
      <h3 className="font-semibold text-neutral-900">Record the date of death</h3>
      <p className="mt-1 text-sm text-neutral-600">
        The death is already on record; this adds its date, once. The statement of death and the final report are then
        raised from it, overdue at once if the death was long ago. A final report already on file is matched to it.
      </p>

      <label className={`${label} mt-3`} htmlFor="death-date-only">Date of death</label>
      <input id="death-date-only" type="date" className={field} value={date} max={todayLocal()}
             onChange={(e) => setDate(e.target.value)} />
      <p className="mt-1 text-xs text-neutral-500">Required, and deliberately not filled in.</p>

      <label className={`${label} mt-3`} htmlFor="death-date-reason">Where the date comes from</label>
      <input id="death-date-reason" className={field} value={reason} maxLength={2000}
             onChange={(e) => setReason(e.target.value)} placeholder="The death certificate, the facility's letter…" />

      <label className={`${label} mt-4`} htmlFor="death-date-surname">
        Type the client’s surname to confirm
      </label>
      <input id="death-date-surname" className={field} value={surname} autoComplete="off"
             onChange={(e) => setSurname(e.target.value)} placeholder="Surname" />
      <SurnameMismatch wrong={check.wrong} />
      <p className="mt-1 text-xs text-neutral-500">
        Correcting a date once it is recorded is not open yet.
      </p>

      <Failure error={record.error} />
      <div className="mt-3 flex gap-2">
        <button type="submit" disabled={!ready || record.isPending}
                className="min-h-[48px] flex-1 rounded-lg bg-neutral-900 px-4 font-semibold text-white
                           disabled:bg-neutral-200 disabled:text-neutral-600 disabled:cursor-not-allowed">
          {record.isPending ? 'Recording…' : 'Record the date of death'}
        </button>
        <button type="button" onClick={onCancel} className={quiet}>Cancel</button>
      </div>
    </form>
  );
}

function DischargeForm({
  clientId, ending, onCancel, onDone,
}: { clientId: string; ending: Ending; onCancel: () => void; onDone: () => void }) {
  const record = useRecordDischarge(clientId);
  const [signed, setSigned] = useState('');
  // A-#171: court, county, index number and judge start from the client's latest live order, each editable; with no
  // order on record they start empty. A-#170: the county is a choice from the catalogue, never typed (the code sent,
  // the label shown), pre-selected only when the order's code is in the list.
  const from = ending.latest_order;
  const [court, setCourt] = useState(from?.court_name ?? '');
  const [county, setCounty] = useState(() =>
    from?.county_code && ending.counties.some((c) => c.code === from.county_code) ? from.county_code : '');
  const [index, setIndex] = useState(from?.court_index_number ?? '');
  const [judge, setJudge] = useState(from?.judge_name ?? '');
  // A-#171: the surname typed back, as a death's is: never pre-filled; the server compares it as record_death() does.
  const [surname, setSurname] = useState('');

  const check = useSurnameCheck(clientId, surname);
  const ready = signed.length === 10 && court.trim() !== '' && county !== '' && check.ok;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!ready) return;
        record.mutate(
          {
            confirm_last_name: surname.trim(),
            signed_date: signed,
            court_name: court.trim(),
            county,
            ...(index.trim() ? { court_index_number: index.trim() } : {}),
            ...(judge.trim() ? { judge_name: judge.trim() } : {}),
          },
          { onSuccess: onDone },
        );
      }}
    >
      <h3 className="font-semibold text-neutral-900">Record the Order Discharging Guardian</h3>
      <p className="mt-1 text-sm text-neutral-600">
        A discharge is an order, not a status. This records the order the judge signed;
        everything below follows from it.
      </p>

      <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
        <p className="font-semibold text-neutral-900">What this sets in motion</p>
        <ul className="mt-1 space-y-0.5 text-sm text-neutral-700">
          {ending.discharge_effects.map((e) => (
            <li key={e.effect}>
              <span className="font-semibold">{e.effect}</span>
              <span className="text-neutral-600"> — {e.detail}</span>
            </li>
          ))}
        </ul>
      </div>

      <label className={`${label} mt-3`} htmlFor="dis-signed">Date the order was signed</label>
      <input id="dis-signed" type="date" className={field} value={signed} max={todayLocal()}
             onChange={(e) => setSigned(e.target.value)} />
      <p className="mt-1 text-xs text-neutral-500">
        Required. The guardianship ends on this date, not today.
      </p>

      <label className={`${label} mt-3`} htmlFor="dis-court">Court</label>
      <input id="dis-court" className={field} value={court} maxLength={128}
             onChange={(e) => setCourt(e.target.value)} placeholder="Supreme Court" />

      <label className={`${label} mt-3`} htmlFor="dis-county">County</label>
      <CountySelect id="dis-county" className={field} value={county} onChange={setCounty} counties={ending.counties} />

      <label className={`${label} mt-3`} htmlFor="dis-index">Index number</label>
      <input id="dis-index" className={field} value={index} maxLength={64}
             onChange={(e) => setIndex(e.target.value)} />

      <label className={`${label} mt-3`} htmlFor="dis-judge">Judge</label>
      <input id="dis-judge" className={field} value={judge} maxLength={128}
             onChange={(e) => setJudge(e.target.value)} />

      {/* The last gate, as the death form's (A-#171): typed, not ticked, and never filled in. */}
      <label className={`${label} mt-4`} htmlFor="dis-surname">
        Type the client’s surname to confirm
      </label>
      <input id="dis-surname" className={field} value={surname} autoComplete="off"
             onChange={(e) => setSurname(e.target.value)} placeholder="Surname" />
      <SurnameMismatch wrong={check.wrong} />

      <Failure error={record.error} />
      <div className="mt-3 flex gap-2">
        <button type="submit" disabled={!ready || record.isPending}
                className="min-h-[48px] flex-1 rounded-lg bg-neutral-900 px-4 font-semibold text-white
                           disabled:bg-neutral-200 disabled:text-neutral-600 disabled:cursor-not-allowed">
          {record.isPending ? 'Recording…' : 'Record the discharge order'}
        </button>
        <button type="button" onClick={onCancel} className={quiet}>Cancel</button>
      </div>
    </form>
  );
}

function Failure({ error }: { error: Error | null }) {
  if (!error) return null;
  const api = error instanceof ApiError ? error : null;
  return (
    <p className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-900">
      {error.message}
      {api?.hint && <span className="block text-rose-700">{api.hint}</span>}
    </p>
  );
}
